package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.PendingFile
import com.arena_web.data.dto.ChatDto
import com.arena_web.data.dto.MessageDto
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.components.widgets.Divider
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.components.widgets.SkeletonBox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodySMedium
import com.arena_web.ui.theme.bodySRegular
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextInput
import org.w3c.dom.events.Event
import kotlin.js.Date

@Composable
fun ChatRoomPanel(
    chat: ChatDto?,
    messages: List<MessageDto>,
    messageText: String,
    typingUsers: Map<String, String>,
    onlineUserIds: Set<String>,
    currentUserId: String?,
    isLoading: Boolean,
    loadingMore: Boolean,
    hasMoreMessages: Boolean,
    editingMessage: MessageDto? = null,
    showMessageSearch: Boolean = false,
    messageSearchQuery: String = "",
    messageSearchResults: List<MessageDto> = emptyList(),
    showChatMenu: Boolean = false,
    pinnedMessage: MessageDto? = null,
    replyingToMessage: MessageDto? = null,
    pendingFile: PendingFile? = null,
    uploadingFile: Boolean = false,
    onTextChanged: (String) -> Unit,
    onSend: () -> Unit,
    onTyping: () -> Unit,
    onLoadMore: () -> Unit,
    onDeleteMessage: (String) -> Unit = {},
    onEditMessage: (MessageDto) -> Unit = {},
    onCancelEdit: () -> Unit = {},
    onSubmitEdit: () -> Unit = {},
    onSearchClick: () -> Unit = {},
    onSearchQueryChanged: (String) -> Unit = {},
    onMenuClick: () -> Unit = {},
    onMuteChat: () -> Unit = {},
    onLeaveChat: () -> Unit = {},
    onDismissMenu: () -> Unit = {},
    onReaction: ((String, String) -> Unit)? = null,
    onReplyMessage: ((MessageDto) -> Unit)? = null,
    onCancelReply: () -> Unit = {},
    onPinMessage: ((String) -> Unit)? = null,
    onAttachmentClick: (() -> Unit)? = null,
    onSendFile: (() -> Unit)? = null,
    onCancelFile: (() -> Unit)? = null,
    onInfoClick: () -> Unit = {},
    showInfoPanel: Boolean = false,
    chatsLoading: Boolean = false,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    if (chat == null) {
        // No chat selected: show the brand loader while the chat list is
        // still being fetched, otherwise prompt the user to pick a chat.
        Box(
            modifier = modifier.fillMaxSize()
                .styleModifier {
                    property("border-radius", "0 ${s(16)} ${s(16)} 0")
                                        },
            contentAlignment = Alignment.Center
        ) {
            if (chatsLoading) {
                LoadingSpinner(size = 80.px)
            } else {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    TintedGlyph(src = AppIcons.Chat, sizePx = 64)
                    SpanText(
                        t.chatSelectConversation,
                        modifier = Modifier.bodyLMedium().color(AppColors.Text.Muted).sMarginTop(16)
                    )
                }
            }
        }
        return
    }

    val isGroup = chat.type.uppercase() == "GROUP"

    Column(modifier = modifier.fillMaxSize().styleModifier {
        property("border-radius", if (showInfoPanel) "0 0 ${s(16)} 0" else "0 ${s(16)} ${s(16)} 0")
        property("transition", "border-radius 0.3s ease")
        property("overflow", "hidden")
    }) {
        // Header with wired actions
        ChatHeader(
            chat = chat,
            typingUsers = typingUsers,
            onlineCount = chat.members.count { onlineUserIds.contains(it.userId) },
            currentUserId = currentUserId,
            showInfoPanel = showInfoPanel,
            onInfoClick = onInfoClick
        )

        // Pinned message bar
        if (pinnedMessage != null) {
            PinnedMessageBar(message = pinnedMessage)
        }

        // Search bar (when active)
        if (showMessageSearch) {
            MessageSearchBar(
                query = messageSearchQuery,
                results = messageSearchResults,
                onQueryChanged = onSearchQueryChanged,
                onClose = onSearchClick
            )
        }

        // Messages area
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("flex", "1")
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("display", "flex")
                    property("flex-direction", "column-reverse")
                    property("scrollbar-width", "none")
                }
                .sPadding(8, 16)
                .id("chat-messages-container")
        ) {
            Column(modifier = Modifier.fillMaxWidth()) {
                if (loadingMore) {
                    Box(
                        modifier = Modifier.fillMaxWidth().sPadding(8, 0),
                        contentAlignment = Alignment.Center
                    ) {
                        InlineSpinner(size = 20.px)
                    }
                }

                if (!isLoading) {
                    var lastDateKey = ""

                    messages.forEachIndexed { index, message ->
                        val dateKey = getDateKey(message.createdAt)
                        if (dateKey != lastDateKey) {
                            lastDateKey = dateKey
                            DateDivider(getDateLabel(message.createdAt))
                        }

                        val isOwn = message.senderId == currentUserId
                        val prevMessage = messages.getOrNull(index - 1)
                        val nextMessage = messages.getOrNull(index + 1)
                        val showSender = isGroup && !isOwn && (
                                prevMessage == null ||
                                        prevMessage.senderId != message.senderId ||
                                        prevMessage.type == "SYSTEM" ||
                                        getDateKey(prevMessage.createdAt) != dateKey
                                )

                        // Grouping: same sender, same date, not system
                        val prevSameSender = prevMessage != null &&
                                prevMessage.senderId == message.senderId &&
                                prevMessage.type != "SYSTEM" &&
                                message.type != "SYSTEM" &&
                                getDateKey(prevMessage.createdAt) == dateKey
                        val nextSameSender = nextMessage != null &&
                                nextMessage.senderId == message.senderId &&
                                nextMessage.type != "SYSTEM" &&
                                message.type != "SYSTEM" &&
                                getDateKey(nextMessage.createdAt) == dateKey

                        val groupPosition = when {
                            prevSameSender && nextSameSender -> GroupPosition.MIDDLE
                            !prevSameSender && nextSameSender -> GroupPosition.FIRST
                            prevSameSender && !nextSameSender -> GroupPosition.LAST
                            else -> GroupPosition.SINGLE
                        }

                        // Look up the sender's player card from chat.members
                        // so the group-chat avatar matches the followers/chat
                        // header treatment. For own messages and direct chats
                        // these stay null and MessageBubble keeps its existing
                        // (no-avatar) branch.
                        val senderMember = if (isGroup && !isOwn) {
                            chat.members.firstOrNull { it.userId == message.senderId }
                        } else null

                        MessageBubble(
                            message = message,
                            isOwnMessage = isOwn,
                            isGroupChat = isGroup,
                            showSenderInfo = showSender,
                            groupPosition = groupPosition,
                            currentUserId = currentUserId,
                            senderCardImageUrl = senderMember?.cardImageUrl,
                            senderCardAvatarUrl = senderMember?.cardAvatarUrl,
                            onEdit = if (isOwn) {{ onEditMessage(message) }} else null,
                            onDelete = if (isOwn) {{ onDeleteMessage(message.id) }} else null,
                            onReaction = if (onReaction != null) {{ emoji -> onReaction(message.id, emoji) }} else null,
                            onReply = if (onReplyMessage != null) {{ onReplyMessage(message) }} else null,
                            onPin = if (onPinMessage != null) {{ onPinMessage(message.id) }} else null
                        )
                    }
                }
            }
        }

        // File preview bar
        if (pendingFile != null) {
            FilePreviewBar(
                file = pendingFile,
                uploading = uploadingFile,
                onSend = { onSendFile?.invoke() },
                onCancel = { onCancelFile?.invoke() }
            )
        }

        // Reply bar
        if (replyingToMessage != null) {
            ReplyBar(
                message = replyingToMessage,
                onCancel = onCancelReply
            )
        }

        // Edit indicator bar
        if (editingMessage != null) {
            EditingBar(
                message = editingMessage,
                onCancel = onCancelEdit
            )
        }

        // Input bar
        MessageInputBar(
            messageText = messageText,
            onTextChanged = onTextChanged,
            onSend = if (editingMessage != null) onSubmitEdit else onSend,
            onTyping = onTyping,
            isEditing = editingMessage != null,
            onAttachmentClick = onAttachmentClick
        )
    }

    // Auto-scroll to newest. With flex-direction column-reverse, the bottom of
    // the chat is scrollTop = 0. Triggered by the last message ID (not size)
    // so loadMore — which prepends older messages — never bounces the viewport.
    //  - First load: scroll to bottom
    //  - New last message is mine: always scroll
    //  - New last message is someone else's AND we're within ~5 messages of
    //    the bottom (400px): scroll. Otherwise leave the user's history view
    //    untouched.
    val lastMessageId = messages.lastOrNull()?.id
    var previousLastId by remember(chat.id) { mutableStateOf<String?>(null) }
    LaunchedEffect(lastMessageId) {
        if (lastMessageId == null || lastMessageId == previousLastId) {
            previousLastId = lastMessageId
            return@LaunchedEffect
        }
        val isFirstLoad = previousLastId == null
        val container = document.getElementById("chat-messages-container")
        val newIsOwn = messages.lastOrNull()?.senderId == currentUserId
        val nearBottom = container?.let { it.scrollTop > -400.0 } ?: true
        if (isFirstLoad || newIsOwn || nearBottom) {
            window.setTimeout({
                document.getElementById("chat-messages-container")?.scrollTop = 0.0
            }, 50)
        }
        previousLastId = lastMessageId
    }

    // Scroll-to-load-more
    LaunchedEffect(chat.id) {
        val handler: (Event) -> Unit = { _ ->
            val container = document.getElementById("chat-messages-container")
            if (container != null) {
                val scrollTop = container.scrollTop
                val scrollHeight = container.scrollHeight.toDouble()
                val clientHeight = container.clientHeight.toDouble()
                if (scrollTop < -(scrollHeight - clientHeight - 100) && hasMoreMessages && !loadingMore) {
                    onLoadMore()
                }
            }
        }
        document.getElementById("chat-messages-container")?.addEventListener("scroll", handler)
    }
}

// ── Pinned message bar ──

@Composable
fun PinnedMessageBar(message: MessageDto) {
    val sportColors = LocalSportColors.current

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(10, 20)
            .backgroundColor(AppColors.Dark.SurfaceVariant)
            .styleModifier {
                property("border-bottom", "none")
                property("flex-shrink", "0")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Pin icon
        Image(
            src = AppIcons.Pin,
            modifier = Modifier
                .sSize(16)
                .styleModifier {
                    property("filter", "brightness(0) saturate(100%) invert(75%) sepia(60%) saturate(500%) hue-rotate(5deg) brightness(105%)")
                    property("flex-shrink", "0")
                }
        )

        // Pinned text
        SpanText(
            message.content,
            modifier = Modifier
                .bodySMedium()
                .color(AppColors.Text.Primary)
                .sPaddingLeft(10)
                .whiteSpace(WhiteSpace.NoWrap)
                .overflow(Overflow.Hidden)
                .textOverflow(TextOverflow.Ellipsis)
        )
    }
}

// ── Reply bar ──

@Composable
fun ReplyBar(message: MessageDto, onCancel: () -> Unit) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(8, 16)
            .backgroundColor(AppColors.Dark.SurfaceVariant)
            .styleModifier {
                property("border-top", "none")
                property("flex-shrink", "0")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .sWidth(3)
                .sHeight(32)
                .sBorderRadius(2)
                .backgroundColor(sportColors.primary)
        )
        Column(modifier = Modifier.sPaddingLeft(10).weight(1f)) {
            SpanText(
                t.chatReplyTo(message.senderUsername ?: t.chatUnknownSender),
                modifier = Modifier.bodySRegular().color(sportColors.primary)
            )
            SpanText(
                message.content,
                modifier = Modifier
                    .bodyMRegular()
                    .color(AppColors.Text.Muted)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("max-width", "400px")
                    }
            )
        }
        HoverIconButton(onClick = onCancel)
    }
}

// ── File preview bar ──

@Composable
fun FilePreviewBar(
    file: PendingFile,
    uploading: Boolean,
    onSend: () -> Unit,
    onCancel: () -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(8, 16)
            .backgroundColor(AppColors.Dark.SurfaceVariant)
            .styleModifier {
                property("border-top", "none")
                property("flex-shrink", "0")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // File icon
        Image(
            src = AppIcons.Document,
            modifier = Modifier
                .sSize(20)
                .styleModifier { property("filter", "brightness(0.6)") }
        )

        // File name
        SpanText(
            file.name,
            modifier = Modifier
                .bodySMedium()
                .color(AppColors.Text.Primary)
                .sPaddingLeft(8)
                .weight(1f)
                .styleModifier {
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                }
        )

        if (uploading) {
            Row(
                modifier = Modifier.sGap(8).sPaddingRight(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                InlineSpinner(size = 16.px)
                SpanText(
                    t.chatUploading,
                    modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
                )
            }
        } else {
            // Send button
            Box(
                modifier = Modifier
                    .sSize(28)
                    .sBorderRadius(6)
                    .backgroundColor(sportColors.primary)
                    .cursor(Cursor.Pointer)
                    .onClick { onSend() },
                contentAlignment = Alignment.Center
            ) {
                Image(
                    src = AppIcons.ArrowUp,
                    modifier = Modifier.sSize(14).styleModifier {
                        property("filter", sportColors.iconFilterOnPrimary)
                    }
                )
            }

            // Cancel button
            HoverIconButton(
                onClick = onCancel,
                modifier = Modifier.sMarginLeft(6)
            )
        }
    }
}

// ── Editing bar ──

@Composable
fun EditingBar(message: MessageDto, onCancel: () -> Unit) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(8, 16)
            .backgroundColor(AppColors.Dark.SurfaceVariant)
            .styleModifier {
                property("border-top", "none")
                property("flex-shrink", "0")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .sWidth(3)
                .sHeight(32)
                .sBorderRadius(2)
                .backgroundColor(sportColors.primary)
        )
        Column(modifier = Modifier.sPaddingLeft(10).weight(1f)) {
            SpanText(
                t.chatEditingMessage,
                modifier = Modifier.bodySRegular().color(sportColors.primary)
            )
            SpanText(
                message.content,
                modifier = Modifier
                    .bodyMRegular()
                    .color(AppColors.Text.Muted)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("max-width", "400px")
                    }
            )
        }
        HoverIconButton(onClick = onCancel)
    }
}

// ── Message search bar ──

@Composable
private fun MessageSearchBar(
    query: String,
    results: List<MessageDto>,
    onQueryChanged: (String) -> Unit,
    onClose: () -> Unit
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPadding(8, 16)
            .backgroundColor(AppColors.Dark.SurfaceVariant)
            .styleModifier {
                property("border-bottom", "none")
                property("flex-shrink", "0")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            src = AppIcons.Search,
            modifier = Modifier.sSize(16).styleModifier {
                property("filter", "brightness(0.5)")
                property("flex-shrink", "0")
            }
        )
        TextInput(
            value = query,
            attrs = Modifier
                .fillMaxWidth()
                .bodyMRegular()
                .color(AppColors.Text.Primary)
                .sPaddingLeft(8)
                .styleModifier {
                    property("background", "transparent")
                    property("border", "none")
                    property("outline", "none")
                    property("font-family", "Urbanist, sans-serif")
                }
                .toAttrs {
                    attr("placeholder", t.chatSearchInChatPlaceholder)
                    attr("autofocus", "true")
                    onInput { e ->
                        onQueryChanged(e.target.asDynamic().value as String)
                    }
                }
        )
        if (results.isNotEmpty()) {
            SpanText(
                t.chatSearchResultsFound(results.size),
                modifier = Modifier.bodySRegular().color(AppColors.Text.Muted).sPaddingRight(8)
            )
        }
        HoverIconButton(onClick = onClose)
    }
}

// ── Date divider ──

@Composable
private fun DateDivider(label: String) {
    Row(
        modifier = Modifier.fillMaxWidth().sPadding(12, 0),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(modifier = Modifier.weight(1f).sHeight(1).backgroundColor(AppColors.Dark.Border))
        SpanText(
            label,
            modifier = Modifier
                .bodySRegular()
                .color(AppColors.Text.Muted)
                .backgroundColor(AppColors.Dark.SurfaceVariant)
                .sBorderRadius(10)
                .sPadding(4, 12)
        )
        Box(modifier = Modifier.weight(1f).sHeight(1).backgroundColor(AppColors.Dark.Border))
    }
}

// ── Date helpers ──

private fun getDateKey(iso: String): String {
    val date = Date(iso)
    return "${date.getFullYear()}-${date.getMonth()}-${date.getDate()}"
}

private fun getDateLabel(iso: String): String {
    val date = Date(iso)
    val now = Date()

    val isToday = date.getFullYear() == now.getFullYear() &&
            date.getMonth() == now.getMonth() &&
            date.getDate() == now.getDate()
    if (isToday) return "Today"

    val yesterday = Date(now.getTime() - 86400000.0)
    val isYesterday = date.getFullYear() == yesterday.getFullYear() &&
            date.getMonth() == yesterday.getMonth() &&
            date.getDate() == yesterday.getDate()
    if (isYesterday) return "Yesterday"

    val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    return "${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}"
}

// ── Skeleton message row ──

@Composable
private fun MessageSkeletonRow(alignEnd: Boolean, width: Int) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .then(
                if (alignEnd) Modifier.styleModifier { property("justify-content", "flex-end") }
                else Modifier
            )
    ) {
        Column(modifier = Modifier.sGap(6)) {
            SkeletonBox(width = width.px, height = 40.px, borderRadius = 12.px)
            SkeletonBox(
                modifier = if (alignEnd) Modifier.styleModifier { property("align-self", "flex-end") } else Modifier,
                width = 60.px, height = 10.px, borderRadius = 4.px
            )
        }
    }
}

// ── Hover icon button (close/cancel) ──

@Composable
private fun HoverIconButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    var hovered by remember { mutableStateOf(false) }
    Box(
        modifier = modifier
            .sSize(28)
            .sBorderRadius(6)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .attrsModifier {
                onMouseEnter { hovered = true }
                onMouseLeave { hovered = false }
            }
            .then(
                if (hovered) Modifier.backgroundColor(Color("rgba(255,255,255,0.08)"))
                else Modifier
            )
            .styleModifier {
                property("transition", "background-color 0.15s ease")
            },
        contentAlignment = Alignment.Center
    ) {
        Image(
            src = AppIcons.Close,
            modifier = Modifier.sSize(14).styleModifier {
                property("filter", "brightness(0.5)")
            }
        )
    }
}
